import { Alert, Flex, Typography, FormInstance, Form } from "antd";
import { observer } from "mobx-react-lite";
import { ReactElement, useEffect, useState } from "react";
import { CONTRACT_STATUS_ENUM, UnkValidationResponse } from "interfaces";
import { useStores } from "stores";
import { contractMessages, ContractMessageConfig } from "constants/contractMessages";
import { CustomAlert } from "ui/CustomAlert";

type AlertType = "success" | "info" | "warning" | "error";

interface AlertMessage {
  content: ReactElement;
  type: AlertType;
}

interface IProps {
  form?: FormInstance;
  unkData?: UnkValidationResponse;
  infoPage?: boolean;
}

const formatMessage = (config: ContractMessageConfig, replacements?: Record<string, string>): AlertMessage => {
  const { text, type } = config;

  let formattedText = text;
  if (replacements) {
    Object.entries(replacements).forEach(([key, value]) => {
      formattedText = formattedText.replace(`{${key}}`, value);
    });
  }

  const content = <Typography.Text strong>{formattedText}</Typography.Text>;
  return { content, type };
};

export const ActionContractNotifier = observer(({ form, unkData, infoPage = false }: IProps) => {
  const { contractCard, user, companyCard } = useStores();
  const { card } = contractCard;
  const { status } = card;
  const [alertMessages, setAlertMessages] = useState<AlertMessage[]>([]);

  const formDirection = form ? Form.useWatch("direction", form) : undefined;

  const { isJuniorOperator, isSeniorOperator, isRiskManager, isJuniorCustomer, isSeniorCustomer } = user.roles;

  const isOperator = isJuniorOperator || isSeniorOperator || isRiskManager;
  const isCustomer = isJuniorCustomer || isSeniorCustomer;

  const contractIsDraft = status === CONTRACT_STATUS_ENUM.DRAFT;

  const contractIsDraftDeclined = status === CONTRACT_STATUS_ENUM.DRAFT_DECLINED;

  const showDeclineNotification = contractIsDraftDeclined && isCustomer && infoPage;

  useEffect(() => {
    if (unkData && !companyCard.card.id && user.userInfo?.company_id) {
      companyCard.fetchCompanyCardById(user.userInfo.company_id);
    }
  }, [unkData, companyCard, user.userInfo?.company_id]);

  useEffect(() => {
    const messages: AlertMessage[] = [];

    if (contractIsDraft && infoPage) {
      if (isOperator) {
        messages.push(formatMessage(contractMessages.draftForOperator));
      } else if (isCustomer) {
        messages.push(formatMessage(contractMessages.draftForCustomer));
      }
    }

    if (contractIsDraft && unkData && companyCard.card.id && ((isOperator && infoPage) || !infoPage)) {
      if (unkData.companyType && companyCard.card.type !== unkData.companyType) {
        messages.push(formatMessage(contractMessages.companyTypeMismatch));
      }

      const contractDirection = card.direction;
      const currentDirection = formDirection || contractDirection;

      if (unkData.direction && currentDirection && currentDirection !== unkData.direction) {
        messages.push(formatMessage(contractMessages.directionMismatch));
      }

      if (!unkData.bankName && unkData.bankRegNum && isOperator) {
        messages.push(
          formatMessage(contractMessages.unkRegisteredInOtherBank, {
            bankRegNum: unkData.bankRegNum,
          })
        );
      }
    }

    setAlertMessages(messages);
  }, [isCustomer, isOperator, contractIsDraft, unkData, companyCard.card, formDirection, card.direction, infoPage]);

  if (alertMessages.length === 0) {
    return null;
  }

  return (
    <>
      {alertMessages.length > 0 && (
        <Flex gap="small" id="contract-action-notifier">
          {showDeclineNotification && (
            <CustomAlert
              message={
                <Typography.Text strong>
                  Прочтите комментарии оператора и актуализируйте информацию в договоре.
                </Typography.Text>
              }
              variant="purple"
              showIcon
            />
          )}
          <Flex vertical gap="small">
            <Flex gap="small">
              {alertMessages
                .filter((alertMessage) => alertMessage.type === "info")
                .map((alertMessage, key) => (
                  <CustomAlert message={alertMessage.content} variant="purple" showIcon key={`info-${key}`} />
                ))}
            </Flex>
            <Flex gap="small">
              {alertMessages
                .filter((alertMessage) => alertMessage.type === "warning")
                .map((alertMessage, key) => (
                  <Alert message={alertMessage.content} type={alertMessage.type} showIcon key={`warning-${key}`} />
                ))}
            </Flex>
          </Flex>
        </Flex>
      )}
    </>
  );
});
